import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { api } from '../services/api';
import { Panel, PanelHeader, PanelBody, PageHeader } from '../components/ui/Panel';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import Pill, { PillTone } from '../components/ui/Pill';
import { EmptyState, SkeletonRows, ErrorNotice, InfoNotice } from '../components/ui/States';
import { fmt, utcStamp, dateShort } from '../lib/format';
import { canMutate } from '../lib/roles';
import { useAuth } from '../contexts/AuthContext';
import type {
  DataResponse,
  MessageListQuery,
  MessageLogEntry as Message,
  MessagePage,
  MessageStatus,
  Suppression,
  SuppressionListQuery,
  SuppressionScope,
  SuppressRequest,
  SuppressResult as SuppressPreview,
  MessageTemplatesResponse,
} from '../contracts';

type Status = MessageStatus;

type Tab = 'log' | 'unsubscribes' | 'templates';

const TABS: Record<Tab, string> = { log: 'Sent log', unsubscribes: 'Unsubscribes', templates: 'Templates' };

const STATUS: Record<Status, { label: string; tone: PillTone }> = {
  sent: { label: 'Sent', tone: 'healthy' },
  not_sent: { label: 'Not sent (outbox)', tone: 'planned' },
  failed: { label: 'Failed', tone: 'failed' },
  suppressed: { label: 'Unsubscribed', tone: 'inactive' },
};

const SCOPE_LABEL = { bulk: 'Bulk only', all: 'All email' };

const inputClass = 'border border-line bg-white px-3 py-[9px] text-table w-full';

function errorMessage(err: unknown, fallback: string) {
  return (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? fallback;
}

function MessageLog() {
  const [status, setStatus] = useState<Status | ''>('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<Message | null>(null);
  const log = useQuery<MessagePage>(
    ['messages', status, q, page],
    async () =>
      (
        await api.get<DataResponse<MessagePage>>('/messages', {
          params: { status: status || undefined, q: q || undefined, page } satisfies MessageListQuery,
        })
      ).data.data,
  );
  const pages = log.data ? Math.max(1, Math.ceil(log.data.total / log.data.limit)) : 1;

  return (
    <Panel>
      <PanelHeader title="Messages" note={log.data ? `${fmt(log.data.total)} total` : undefined}>
        <select
          className="border border-line-strong bg-white px-2 py-[6px] text-small"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as Status | '');
            setPage(1);
          }}
        >
          <option value="">Every status</option>
          {(Object.keys(STATUS) as Status[]).map((s) => (
            <option key={s} value={s}>
              {STATUS[s].label}
            </option>
          ))}
        </select>
      </PanelHeader>
      <PanelBody>
        {log.data?.outbox ? (
          <div className="mb-4">
            <InfoNotice>Outbox mode: email is written to this log but not delivered.</InfoNotice>
          </div>
        ) : null}
        <input
          className={`${inputClass} mb-3`}
          placeholder="Search by recipient or subject"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
        />
        {log.isLoading ? (
          <SkeletonRows rows={6} />
        ) : log.error ? (
          <ErrorNotice>{errorMessage(log.error, 'Could not load messages.')}</ErrorNotice>
        ) : !log.data?.messages.length ? (
          <EmptyState title="No messages">Nothing has been sent{status || q ? ' that matches' : ' yet'}.</EmptyState>
        ) : (
          <>
            <table className="w-full">
              <thead>
                <tr className="text-left text-small text-ink-2 border-b border-line-strong">
                  <th className="py-2 pr-4">When (UTC)</th>
                  <th className="py-2 pr-4">To</th>
                  <th className="py-2 pr-4">Subject</th>
                  <th className="py-2 pr-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {log.data.messages.map((m) => (
                  <tr key={m.id} className="border-b border-line text-table align-top">
                    <td className="py-[9px] pr-4 whitespace-nowrap tabular text-ink-2">{utcStamp(m.createdAt)}</td>
                    <td className="py-[9px] pr-4">{m.toName ? `${m.toName} · ${m.toEmail}` : m.toEmail}</td>
                    <td className="py-[9px] pr-4">
                      <button type="button" className="underline text-left" onClick={() => setOpen(m)}>
                        {m.subject}
                      </button>
                      <div className="text-small text-ink-2">{m.templateKey}</div>
                    </td>
                    <td className="py-[9px] pr-4">
                      <Pill tone={STATUS[m.status].tone}>{STATUS[m.status].label}</Pill>
                      {m.reason && m.status === 'failed' ? (
                        <div className="text-small text-ink-2 mt-1">{m.reason}</div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {pages > 1 ? (
              <div className="flex items-center gap-3 mt-4 text-small text-ink-2">
                <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                  Previous
                </Button>
                Page {page} of {pages}
                <Button size="sm" variant="ghost" disabled={page >= pages} onClick={() => setPage(page + 1)}>
                  Next
                </Button>
              </div>
            ) : null}
          </>
        )}
      </PanelBody>
      {open ? (
        <Modal title={open.subject} onClose={() => setOpen(null)} width={680}>
          <div className="text-small text-ink-2 mb-3">
            To {open.toEmail} · {utcStamp(open.createdAt)} UTC · {STATUS[open.status].label}
          </div>
          <pre className="whitespace-pre-wrap text-table font-sans">{open.bodyText ?? '(body not kept)'}</pre>
        </Modal>
      ) : null}
    </Panel>
  );
}

function Unsubscribes({ mutable }: { mutable: boolean }) {
  const queryClient = useQueryClient();
  const [text, setText] = useState('');
  const [scope, setScope] = useState<SuppressionScope>('bulk');
  const [reason, setReason] = useState('');
  const [preview, setPreview] = useState<SuppressPreview | null>(null);
  const [showLifted, setShowLifted] = useState(false);
  const [lifting, setLifting] = useState<Suppression | null>(null);

  const list = useQuery<Suppression[]>(
    ['suppressions', showLifted],
    async () =>
      (
        await api.get<DataResponse<Suppression[]>>('/messages/suppressions', {
          params: { includeLifted: showLifted ? '1' : undefined } satisfies SuppressionListQuery,
        })
      ).data.data,
  );
  const run = useMutation(
    async (dryRun: boolean) =>
      (
        await api.post<DataResponse<SuppressPreview>>('/messages/suppressions', {
          emails: text,
          scope,
          reason: reason || null,
          dryRun,
        } satisfies SuppressRequest)
      ).data.data,
    {
      onSuccess: (data, dryRun) => {
        setPreview(data);
        if (!dryRun) {
          setText('');
          queryClient.invalidateQueries('suppressions');
        }
      },
    },
  );
  const lift = useMutation(async (s: Suppression) => api.post(`/messages/suppressions/${s.id}/lift`), {
    onSuccess: () => {
      setLifting(null);
      queryClient.invalidateQueries('suppressions');
    },
  });
  const pending = preview ? preview.toAdd.length + preview.toRestore.length + preview.toRaise.length : 0;

  return (
    <Panel>
      <PanelHeader title="Unsubscribes" note={list.data ? fmt(list.data.length) : undefined}>
        <label className="flex items-center gap-2 text-small text-ink-2">
          <input type="checkbox" checked={showLifted} onChange={(e) => setShowLifted(e.target.checked)} />
          Show lifted
        </label>
      </PanelHeader>
      <PanelBody>
        <p className="text-small text-ink-2 mb-4 max-w-[80ch]">
          <strong>Bulk only</strong> keeps the address off newsletters and mailing lists; program email (a Fellow’s next
          step, a speaker’s confirmation) still goes. <strong>All email</strong> means the platform never emails the
          address.
        </p>
        {mutable ? (
          <div className="border-b border-line pb-5 mb-5">
            <label className="block text-small text-ink-2 mb-1">
              Add unsubscribes — paste emails, one per line or separated by commas
            </label>
            <textarea
              className={`${inputClass} h-[96px] font-mono text-small`}
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                setPreview(null);
              }}
              placeholder={'someone@university.ca\nanother@hospital.ca'}
            />
            <div className="grid grid-cols-[200px_1fr] gap-3 mt-3">
              <select
                className={inputClass}
                value={scope}
                onChange={(e) => {
                  setScope(e.target.value as 'bulk' | 'all');
                  setPreview(null);
                }}
              >
                <option value="bulk">Bulk only</option>
                <option value="all">All email</option>
              </select>
              <input
                className={inputClass}
                placeholder="Reason (optional), e.g. replied STOP to the newsletter"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-3 mt-3">
              <Button
                variant="outline"
                size="sm"
                disabled={!text.trim() || run.isLoading}
                onClick={() => run.mutate(true)}
              >
                Check emails
              </Button>
              {preview && preview.dryRun && pending ? (
                <Button variant="primary" size="sm" disabled={run.isLoading} onClick={() => run.mutate(false)}>
                  Unsubscribe {pending}
                </Button>
              ) : null}
            </div>
            {run.error ? (
              <div className="mt-3">
                <ErrorNotice>{errorMessage(run.error, 'Could not check those emails.')}</ErrorNotice>
              </div>
            ) : null}
            {preview ? (
              <div className="mt-3 text-small">
                {!preview.dryRun ? (
                  <InfoNotice>
                    {fmt(preview.changed)} address{preview.changed === 1 ? '' : 'es'} unsubscribed (
                    {SCOPE_LABEL[preview.scope]}).
                  </InfoNotice>
                ) : (
                  <div className="text-ink-2">
                    {fmt(preview.toAdd.length)} new · {fmt(preview.toRestore.length)} unsubscribed again ·{' '}
                    {fmt(preview.toRaise.length)} raised to all email · {fmt(preview.already.length)} already
                    unsubscribed
                  </div>
                )}
                {preview.invalid.length ? (
                  <div className="mt-1">
                    <span className="text-warning">Not email addresses:</span> {preview.invalid.join(', ')}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
        {list.isLoading ? (
          <SkeletonRows rows={5} />
        ) : list.error ? (
          <ErrorNotice>Could not load unsubscribes.</ErrorNotice>
        ) : !list.data?.length ? (
          <EmptyState title="No unsubscribes">Nobody has asked to be taken off DIVERT email.</EmptyState>
        ) : (
          <table className="w-full">
            <tbody>
              {list.data.map((s) => (
                <tr key={s.id} className="border-b border-line text-table">
                  <td className="py-[9px] pr-4">{s.email}</td>
                  <td className="py-[9px] pr-4">
                    <Pill tone={s.liftedAt ? 'inactive' : s.scope === 'all' ? 'failed' : 'planned'}>
                      {s.liftedAt ? 'Lifted' : SCOPE_LABEL[s.scope]}
                    </Pill>
                  </td>
                  <td className="py-[9px] pr-4 text-ink-2">{s.reason ?? ''}</td>
                  <td className="py-[9px] pr-4 text-ink-2 tabular whitespace-nowrap">{dateShort(s.createdAt)}</td>
                  <td className="py-[9px] pr-4 text-right">
                    {mutable && !s.liftedAt ? (
                      <Button variant="ghost" size="sm" onClick={() => setLifting(s)}>
                        Lift
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </PanelBody>
      {lifting ? (
        <Modal
          title="Lift unsubscribe"
          onClose={() => setLifting(null)}
          footer={
            <div className="flex justify-end gap-3">
              <Button variant="ghost" onClick={() => setLifting(null)}>
                Cancel
              </Button>
              <Button variant="destructive" disabled={lift.isLoading} onClick={() => lift.mutate(lifting)}>
                Lift
              </Button>
            </div>
          }
        >
          <p className="text-table">
            Email <strong>{lifting.email}</strong> again? Only do this when the person asked to be back on the list. The
            record of the unsubscribe is kept.
          </p>
          {lift.error ? (
            <div className="mt-3">
              <ErrorNotice>{errorMessage(lift.error, 'Could not lift the unsubscribe.')}</ErrorNotice>
            </div>
          ) : null}
        </Modal>
      ) : null}
    </Panel>
  );
}

function Templates() {
  const list = useQuery<MessageTemplatesResponse>(
    'message-templates',
    async () => (await api.get<DataResponse<MessageTemplatesResponse>>('/messages/templates')).data.data,
  );
  return (
    <Panel>
      <PanelHeader title="Templates" note={list.data ? fmt(list.data.templates.length) : undefined} />
      <PanelBody>
        {list.isLoading ? (
          <SkeletonRows rows={3} />
        ) : list.error ? (
          <ErrorNotice>Could not load templates.</ErrorNotice>
        ) : (
          list.data?.templates.map((t) => (
            <div key={t.key} className="border-b border-line pb-5 mb-5 last:border-0 last:mb-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-semibold text-table">{t.key}</span>
                <span className="text-small text-ink-2">v{t.version}</span>
                <Pill tone={t.kind === 'bulk' ? 'planned' : 'running'}>{t.kind}</Pill>
                {t.bilingual ? <Pill tone="linked">EN + FR</Pill> : <Pill tone="inactive">English (staff)</Pill>}
              </div>
              <p className="text-small text-ink-2 mb-3">{t.description}</p>
              {t.preview ? (
                <div className="bg-paper border border-line p-4">
                  <div className="text-small text-ink-2 mb-2">Sample · Subject: {t.preview.subject}</div>
                  <pre className="whitespace-pre-wrap text-table font-sans">{t.preview.text}</pre>
                </div>
              ) : null}
            </div>
          ))
        )}
      </PanelBody>
    </Panel>
  );
}

export default function Messages() {
  const { user } = useAuth();
  const mutable = canMutate(user?.role);
  const [tab, setTab] = useState<Tab>(mutable ? 'log' : 'unsubscribes');

  return (
    <>
      <PageHeader
        title="Messages"
        sub="Every email the platform sends, the people who asked not to be emailed, and the templates. Bulk email still goes from your own mail client; the platform only builds the list."
      />
      <div className="flex items-center gap-2 mb-5">
        {(Object.keys(TABS) as Tab[])
          .filter((t) => t !== 'log' || mutable)
          .map((t) => (
            <Button key={t} size="sm" variant={t === tab ? 'dark' : 'ghost'} onClick={() => setTab(t)}>
              {TABS[t]}
            </Button>
          ))}
      </div>
      {tab === 'log' && mutable ? <MessageLog /> : null}
      {tab === 'unsubscribes' ? <Unsubscribes mutable={mutable} /> : null}
      {tab === 'templates' ? <Templates /> : null}
    </>
  );
}
